// Google Sheets API configuration
const API_KEY = 'AIzaSyDde5SCqkccw066wXTOUdyoRqgC5QIwshw';

// Sheet configurations
const SHEETS = {
    main: {
        id: '1rWTKjDAMf5ImWDpAyvlIVU4NrUacBwVEyKsJo5HVF84',
        range: 'Sheet1!A1:A10'
    },
    secondary: {
        id: '1pPvjeqAPZkd37PrE1GbYPWBsrCcGrtnVRrbsC4sfqZg',
        range: 'Sheet1!A1:A10'
    },
    news: {
        id: '1rU7Xh0pj67ia39k4EbM5rJnANDa5aVkpb0MvZ44Nr3I',
        range: 'Sheet1!A1:A10'
    }
};

/**
 * Fetches data from Google Sheets and creates a table
 * @param {string} containerId - The ID of the container element
 * @param {string} sheetKey - The key of the sheet configuration to use
 * @returns {Promise<void>}
 */
export async function loadSheetData(containerId = 'table-container', sheetKey = 'main') {
    const sheetConfig = SHEETS[sheetKey];
    if (!sheetConfig) {
        throw new Error(`Sheet configuration not found for key: ${sheetKey}`);
    }

    const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetConfig.id}?ranges=${encodeURIComponent(sheetConfig.range)}&includeGridData=true&fields=sheets.data.rowData.values&key=${API_KEY}`;
    
    try {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`API Error: ${response.status}`);
        }
        
        const data = await response.json();
        if (!data.sheets?.[0]?.data?.[0]?.rowData) {
            throw new Error('No data found in sheet');
        }

        const table = createTable(data.sheets[0].data[0].rowData);
        const container = document.getElementById(containerId);
        
        if (container) {
            container.innerHTML = '';
            container.appendChild(table);
        }
    } catch (error) {
        console.error('Error loading sheet:', error);
        const container = document.getElementById(containerId);
        if (container) {
            container.innerHTML = `<div class="error-message">Failed to load data: ${error.message}</div>`;
        }
    }
}

/**
 * Creates a table element from sheet data
 * @param {Array} rowData - The sheet row data
 * @returns {HTMLTableElement}
 */
function createTable(rowData) {
    const table = document.createElement('table');
    table.className = 'sheet-table';
    
    // Create body
    const tbody = document.createElement('tbody');
    
    // Start from index 1 to skip the header row
    for (let i = 1; i < rowData.length; i++) {
        const row = document.createElement('tr');
        
        if (rowData[i]?.values) {
            // Check if the entire row is empty
            const hasContent = rowData[i].values.some(cell => 
                cell?.formattedValue || 
                cell?.userEnteredValue || 
                cell?.hyperlink
            );
            
            // Skip empty rows
            if (!hasContent) continue;
            
            rowData[i].values.forEach(cell => {
                // Skip empty cells
                if (!cell?.formattedValue && !cell?.userEnteredValue && !cell?.hyperlink) {
                    return;
                }
                
                const td = document.createElement('td');
                
                // Check for IMAGE formula
                if (cell.userEnteredValue?.formulaValue?.startsWith('=IMAGE(')) {
                    const imageUrl = cell.userEnteredValue.formulaValue.match(/=IMAGE\("([^"]+)"\)/)?.[1];
                    if (imageUrl) {
                        const img = document.createElement('img');
                        img.src = imageUrl;
                        img.alt = 'Embedded image';
                        img.className = 'sheet-image';
                        td.appendChild(img);
                    } else {
                        td.textContent = cell.formattedValue || '';
                    }
                }
                // Check for HYPERLINK formula
                else if (cell.userEnteredValue?.formulaValue?.startsWith('=HYPERLINK(')) {
                    const match = cell.userEnteredValue.formulaValue.match(/=HYPERLINK\("([^"]+)",\s*"([^"]+)"\)/);
                    if (match) {
                        const url = match[1];
                        const text = match[2];
                        const link = document.createElement('a');
                        link.href = url;
                        link.textContent = text;
                        link.target = '_blank';
                        link.rel = 'noopener noreferrer';
                        td.appendChild(link);
                    } else {
                        td.textContent = cell.formattedValue || '';
                    }
                }
                // Check for hyperlink
                else if (cell.hyperlink) {
                    const link = document.createElement('a');
                    link.href = cell.hyperlink;
                    link.textContent = cell.formattedValue || cell.hyperlink;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    td.appendChild(link);
                } else {
                    // Check for URLs in plain text
                    const cellText = cell.formattedValue || '';
                    
                    // Check for markdown-style links [Label](URL)
                    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
                    if (markdownLinkRegex.test(cellText)) {
                        // Reset regex for reuse
                        markdownLinkRegex.lastIndex = 0;
                        let lastIndex = 0;
                        let result;
                        
                        while ((result = markdownLinkRegex.exec(cellText)) !== null) {
                            // Add text before the link
                            if (result.index > lastIndex) {
                                td.appendChild(document.createTextNode(cellText.substring(lastIndex, result.index)));
                            }
                            
                            // Create the link
                            const link = document.createElement('a');
                            link.href = result[2];
                            link.textContent = result[1];
                            link.target = '_blank';
                            link.rel = 'noopener noreferrer';
                            td.appendChild(link);
                            
                            lastIndex = result.index + result[0].length;
                        }
                        
                        // Add remaining text after the last link
                        if (lastIndex < cellText.length) {
                            td.appendChild(document.createTextNode(cellText.substring(lastIndex)));
                        }
                    } else {
                        // Check for plain URLs
                        const urlRegex = /(https?:\/\/[^\s]+)/g;
                        const parts = cellText.split(urlRegex);
                        
                        if (parts.length === 1) {
                            // No URLs found, just plain text
                            td.textContent = cellText;
                        } else {
                            // URLs found, create mixed content
                            parts.forEach((part, index) => {
                                if (part && /^https?:\/\/[^\s]+$/.test(part)) {
                                    // This part is a URL
                                    const link = document.createElement('a');
                                    link.href = part;
                                    link.textContent = part;
                                    link.target = '_blank';
                                    link.rel = 'noopener noreferrer';
                                    td.appendChild(link);
                                } else if (part && part.trim()) {
                                    // This part is plain text
                                    td.appendChild(document.createTextNode(part));
                                }
                            });
                        }
                    }
                }
                
                row.appendChild(td);
            });
        }
        
        tbody.appendChild(row);
    }
    
    table.appendChild(tbody);
    return table;
} 